/**
 * Transient-job registration for the drag-drop / file-picker editor entry.
 *
 * The pipeline-produced editor entry arrives with `?jobId=…` in the URL, so the
 * backend `editor-ir` / `editor-edit` / `editor-download` endpoints (all jobId-
 * keyed) serve it directly. The direct-upload entry (drag-drop a raw PDF, or the
 * "Open" file picker) has NO jobId. Before the WASM editor was deleted, that path
 * parsed the PDF in-browser via Blazor-WASM+iText — the live IP-exposure surface
 * the pdf-editor-no-itext Epic exists to kill.
 *
 * This module bridges the gap (retrofit design §2.2 / §G-Q1 option a): it POSTs
 * the dropped file to `POST /api/editor-transient`, which registers it as a
 * lightweight COMPLETED job and returns `{ jobId }`. The frontend then drives the
 * SAME keyed backend plumbing a pipeline job uses — no client-side PDF parse, no
 * WASM bundle.
 *
 * Per docs/design/pdf-editor-backend-session-retrofit-260715.md §2.2, §G-Q1.
 */
import { appendTokenIfPresent } from "./url-token.js";
/**
 * Register a drag-drop / file-picker PDF as a transient editor job and return
 * its jobId. Throws on any non-2xx response so the caller can surface a toast
 * and fall back to the empty state (there is no in-browser parser to degrade
 * to — that was the WASM path, now deleted).
 *
 * @param file The dropped/picked PDF.
 * @param fetchImpl Injectable fetch for tests; defaults to the global `fetch`.
 * @returns The jobId keying the backend editor endpoints.
 */
export async function registerTransientJob(file, fetchImpl = fetch) {
    return _postTransient("/api/editor-transient", file, fetchImpl);
}
/**
 * Register a drag-drop / file-picker `.odp` as a passthrough editor job and
 * return its jobId. Mirrors {@link registerTransientJob} but posts to the ODP
 * passthrough entry (`POST /api/editor-transient-odp`, libreoffice-odf Phase 5b)
 * — which stores the uploaded bytes as an unremediated COMPLETED job the shared
 * ODP a11y editor opens on (INV-ODF5-PASSTHRU). ODP full remediation is Phase 4
 * (PILOT-gated); this passthrough ships the editor now. Throws on any non-2xx so
 * the caller can surface a toast (there is no in-browser ODP parser to degrade
 * to).
 *
 * @param file The dropped/picked `.odp`.
 * @param fetchImpl Injectable fetch for tests; defaults to the global `fetch`.
 * @returns The jobId keying the backend `editor-ir-odp` / `editor-edit-odp` endpoints.
 */
export async function registerTransientOdpJob(file, fetchImpl = fetch) {
    return _postTransient("/api/editor-transient-odp", file, fetchImpl);
}
/**
 * Shared POST-a-file → `{ jobId }` poster for the transient/passthrough editor
 * entries. Extracted on the SECOND site (rule #11 / A.6): the PDF and ODP entries
 * differ only in the endpoint path.
 */
async function _postTransient(endpoint, file, fetchImpl) {
    const form = new FormData();
    form.append("file", file, file.name);
    const url = appendTokenIfPresent(endpoint);
    const resp = await fetchImpl(url, {
        method: "POST",
        body: form,
        credentials: "same-origin",
    });
    if (!resp.ok) {
        let detail = "";
        try {
            const body = (await resp.json());
            if (typeof body.detail === "string")
                detail = `: ${body.detail}`;
        }
        catch {
            // Non-JSON error body (e.g. a proxy 502) — the status code is enough.
        }
        throw new Error(`${endpoint} failed: HTTP ${resp.status}${detail}`);
    }
    const body = (await resp.json());
    if (typeof body.jobId !== "string" || body.jobId.length === 0) {
        throw new Error(`${endpoint}: response missing jobId`);
    }
    return body.jobId;
}
